import { useState, type FormEvent } from 'react';
import { CheckCircle2, Camera } from 'lucide-react';
import { confirmTutorPhoto, patchMyTutorProfile, signTutorPhotoUpload } from '@/lib/tutor-api';
import { Label } from '@/components/ui/label';
import { FooterButtons, type StepProps } from './shared';
import { ImageCropper } from './ImageCropper';

const PHOTO_MAX_BYTES = 5 * 1024 * 1024;

export function PhotoStep({ profile, accessToken, onSaved, onError }: StepProps) {
  const hasExistingPhoto = !!profile?.photoUrl;
  const [keepExisting, setKeepExisting] = useState(hasExistingPhoto);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [rawImageSrc, setRawImageSrc] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0] ?? null;
    if (!picked) return;

    if (picked.size > 15 * 1024 * 1024) {
      onError(new Error('Original image must be smaller than 15 MB.'));
      return;
    }

    const allowed = ['image/jpeg', 'image/png', 'image/webp'] as const;
    if (!(allowed as ReadonlyArray<string>).includes(picked.type)) {
      onError(new Error('Photo must be JPEG, PNG, or WebP.'));
      return;
    }

    if (rawImageSrc) URL.revokeObjectURL(rawImageSrc);
    setRawImageSrc(URL.createObjectURL(picked));
  };

  const handleCropComplete = (croppedFile: File) => {
    setFile(croppedFile);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(croppedFile));
    if (rawImageSrc) {
      URL.revokeObjectURL(rawImageSrc);
      setRawImageSrc(null);
    }
  };

  const handleCropCancel = () => {
    if (rawImageSrc) {
      URL.revokeObjectURL(rawImageSrc);
      setRawImageSrc(null);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();

    // Keep existing photo path — just mark step complete and advance.
    if (keepExisting && hasExistingPhoto) {
      setBusy(true);
      try {
        const existing = profile!.completedSteps ?? [];
        if (!existing.includes('photo')) {
          const next = await patchMyTutorProfile(accessToken, {
            completedSteps: [...existing, 'photo'],
          });
          onSaved(next);
        } else {
          onSaved(profile!);
        }
      } catch (err) {
        onError(err);
      } finally {
        setBusy(false);
      }
      return;
    }

    // Upload new photo path.
    if (!file) {
      onError(new Error('Please pick a photo to upload.'));
      return;
    }
    if (file.size > PHOTO_MAX_BYTES) {
      onError(new Error('Photo must be 5 MB or smaller.'));
      return;
    }
    const allowed = ['image/jpeg', 'image/png', 'image/webp'] as const;
    type AllowedType = (typeof allowed)[number];
    if (!(allowed as ReadonlyArray<string>).includes(file.type)) {
      onError(new Error('Photo must be JPEG, PNG, or WebP.'));
      return;
    }

    setBusy(true);
    try {
      const signed = await signTutorPhotoUpload(accessToken, {
        contentType: file.type as AllowedType,
        contentLength: file.size,
      });
      const putRes = await fetch(signed.uploadUrl, {
        method: 'PUT',
        headers: signed.headers,
        body: file,
      });
      if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
      const next = await confirmTutorPhoto(accessToken, signed.objectPath);
      onSaved(next);
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="space-y-5" onSubmit={submit}>
      {hasExistingPhoto ? (
        <>
          {/* Show existing photo prominently */}
          <div className="flex items-start gap-4 rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 dark:border-emerald-800/40 dark:bg-emerald-900/10">
            <img
              src={profile!.photoUrl!}
              alt="Your current profile photo"
              className="h-20 w-20 shrink-0 rounded-full object-cover ring-2 ring-emerald-400/40 dark:ring-emerald-600/40"
            />
            <div>
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                  You already have a profile photo
                </p>
              </div>
              <p className="mt-1 text-xs text-emerald-600 dark:text-emerald-500">
                This is how you'll appear to parents in search results.
              </p>
            </div>
          </div>

          {/* Choice: keep or replace */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">What would you like to do?</p>
            <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-slate-200 px-4 py-3 hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-700/40">
              <input
                type="radio"
                name="photoChoice"
                checked={keepExisting}
                onChange={() => { setKeepExisting(true); setFile(null); setPreviewUrl(null); setRawImageSrc(null); }}
                className="h-4 w-4 text-brand-600 focus:ring-brand-600"
              />
              <div>
                <p className="text-sm font-medium text-foreground">Keep my current photo</p>
                <p className="text-xs text-muted-foreground">Continue with the photo already on file</p>
              </div>
            </label>
            <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-slate-200 px-4 py-3 hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-700/40">
              <input
                type="radio"
                name="photoChoice"
                checked={!keepExisting}
                onChange={() => setKeepExisting(false)}
                className="h-4 w-4 text-brand-600 focus:ring-brand-600"
              />
              <div>
                <p className="text-sm font-medium text-foreground">Upload a new photo</p>
                <p className="text-xs text-muted-foreground">Replace the current photo with a different one</p>
              </div>
            </label>
          </div>
        </>
      ) : (
        <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/60">
          <Camera className="mt-0.5 h-5 w-5 shrink-0 text-brand-600" />
          <p className="text-sm text-muted-foreground">
            A friendly, professional photo builds trust with parents and significantly
            increases your booking rate.
          </p>
        </div>
      )}

      {/* File upload section — shown when uploading new */}
      {!keepExisting && (
        <div className="space-y-3">
          {rawImageSrc ? (
            <ImageCropper
              imageSrc={rawImageSrc}
              fileName="profile-photo.png"
              onCancel={handleCropCancel}
              onCrop={handleCropComplete}
            />
          ) : (
            <>
              {/* New file preview */}
              {previewUrl && (
                <div className="flex items-center gap-3">
                  <img
                    src={previewUrl}
                    alt="New photo preview"
                    className="h-16 w-16 rounded-full object-cover ring-2 ring-brand-400/40"
                  />
                  <div>
                    <p className="text-sm font-medium text-foreground">New photo selected (cropped)</p>
                    <p className="text-xs text-muted-foreground">{file?.name}</p>
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="photo-file">Photo file</Label>
                <p className="text-xs text-muted-foreground">JPEG, PNG, or WebP · max 5 MB</p>
                <input
                  id="photo-file"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-slate-600 dark:text-slate-300
                    file:mr-3 file:rounded-lg file:border-0 file:bg-brand-50 file:px-3 file:py-2
                    file:text-sm file:font-medium file:text-brand-700
                    hover:file:bg-brand-100
                    dark:file:bg-brand-900/30 dark:file:text-brand-300 dark:hover:file:bg-brand-900/50"
                />
              </div>
            </>
          )}
        </div>
      )}

      <FooterButtons
        primaryLabel={keepExisting ? 'Keep photo & continue' : 'Upload & continue'}
        busy={busy}
      />
    </form>
  );
}
